import React, { useCallback, useEffect, useRef, useState } from 'react'

import { theme } from '@acorns/grove'
import { ActionStrip } from '@acorns/web-components'
import {
  GenericAnalyticsClient,
  StyledRemoteString,
  getWindow,
  useEnv,
  useRemoteStrings,
} from '@acorns/web-utils'

import { SkipButton } from 'components/SkipButton'
import {
  trackAccountReopenWelcomeBackSurveyNextButtonTappedPayload,
  trackAccountReopenWelcomeBackSurveyScreenViewedPayload,
  trackAccountReopenWelcomeBackSurveySkipButtonTappedPayload,
} from 'generated/trackingEvents'
import {
  GetStep,
  SURVEY_COMPLETED_KEY,
} from 'pages/closed-account/ClosedAccount'
import { reopenFlowTypeInReopenFunnel } from 'utils/analytics/reopenFlowAnalytics'

import {
  Fade,
  StyledDisclaimer,
  SurveyButtonContainer,
  SurveyCardContainer,
  SurveyCheckbox,
  SurveyHeader,
  SurveyInputContainer,
  SurveyList,
  SurveyOptionLabel,
  SurveyPageBackground,
  SurveyScroll,
  SurveyStyledButton,
  SurveySubtitle,
  SurveyTitle,
} from './styled-components'

interface Props {
  setStep: (index: number) => void
  getStep: GetStep
  analytics: GenericAnalyticsClient
  isLongReopen?: boolean
}

type LinkWrapperProps = {
  children?: React.ReactNode
  onClick?: () => void
}

const LinkWrapper = ({ children, ...props }: LinkWrapperProps) => (
  <a {...props}>{children}</a>
)

export const Survey = ({
  setStep,
  getStep,
  analytics,
  isLongReopen,
}: Props) => {
  const { getRemoteString } = useRemoteStrings()
  const { env } = useEnv()
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set())
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const ctaRef = useRef<HTMLDivElement | null>(null)
  const labelRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [multilineIndices, setMultilineIndices] = useState<Set<number>>(
    new Set(),
  )

  const options = getRemoteString({
    key: 'closed_account.reopen_survey.options',
  })

  const reopenFlowType = reopenFlowTypeInReopenFunnel(isLongReopen)

  const storageKey = `surveyOrder:${options}`

  const getOrder = (key: string, rawOptions: string): string[] => {
    const cached = sessionStorage.getItem(key)
    if (cached) {
      return JSON.parse(cached)
    }
    const split = (rawOptions || '')
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)

    const lastOption = split[split.length - 1]
    const shuffleableOptions = split.slice(0, -1)
    shuffleableOptions.sort(() => Math.random() - 0.5)

    const order = [...shuffleableOptions, lastOption]
    sessionStorage.setItem(key, JSON.stringify(order))
    return order
  }

  const [optionsOrder, setOptionsOrder] = useState<string[]>(() =>
    getOrder(storageKey, options),
  )

  useEffect(() => {
    setOptionsOrder(getOrder(storageKey, options))

    analytics.track(
      ...trackAccountReopenWelcomeBackSurveyScreenViewedPayload({
        funnel: 'reopen',
        type: reopenFlowType,
      }),
    )
  }, [storageKey, analytics])

  const computeFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const atTop = el.scrollTop <= 0
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    setShowTopFade(!atTop)
    setShowBottomFade(!atBottom)
  }, [])

  //Set top and bottom fades
  useEffect(() => {
    computeFades()
    const el = scrollRef.current
    if (!el) {
      return
    }
    el.addEventListener('scroll', computeFades)
    window.addEventListener('resize', computeFades)
    return () => {
      el.removeEventListener('scroll', computeFades)
      window.removeEventListener('resize', computeFades)
    }
  }, [computeFades])

  const computeMultiline = useCallback(() => {
    const next = new Set<number>()
    labelRefs.current.forEach((el, idx) => {
      if (!el) {
        return
      }
      const style = window.getComputedStyle(el)
      const lineHeight = parseFloat(style.lineHeight)
      const height = el.clientHeight
      if (height > lineHeight + 1) {
        next.add(idx)
      }
    })
    setMultilineIndices(next)
  }, [])

  //Set option container height based on text wrapping
  useEffect(() => {
    computeMultiline()
    window.addEventListener('resize', computeMultiline)
    return () => {
      window.removeEventListener('resize', computeMultiline)
    }
  }, [computeMultiline])

  // Measure CTA container height and expose as CSS variable for bottom fade and spacing
  useEffect(() => {
    const updateOffset = () => {
      const card = cardRef.current
      const cta = ctaRef.current
      if (!card || !cta) {
        return
      }
      const height = cta.offsetHeight
      card.style.setProperty('--cta-offset', `${height + 1}px`)
    }
    updateOffset()
    window.addEventListener('resize', updateOffset)
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined' && ctaRef.current) {
      ro = new ResizeObserver(updateOffset)
      ro.observe(ctaRef.current)
    }
    return () => {
      window.removeEventListener('resize', updateOffset)
      if (ro && ctaRef.current) {
        ro.unobserve(ctaRef.current)
        ro.disconnect()
      }
    }
  }, [])

  const handleSkip = () => {
    analytics.track(
      ...trackAccountReopenWelcomeBackSurveySkipButtonTappedPayload({
        funnel: 'reopen',
        type: reopenFlowType,
      }),
    )
    sessionStorage.setItem(SURVEY_COMPLETED_KEY, 'true')
    setStep(getStep('Welcome'))
  }

  const handleSubmit = () => {
    const selections = Array.from(selectedIndices).map((i) => optionsOrder[i])
    analytics.track(
      ...trackAccountReopenWelcomeBackSurveyNextButtonTappedPayload({
        domains: selections,
        funnel: 'reopen',
        type: reopenFlowType,
      }),
    )
    sessionStorage.setItem(SURVEY_COMPLETED_KEY, 'true')
    setStep(getStep('Welcome'))
  }

  const handleOptionSelect = useCallback((index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  const handlePrivacyPolicyClick = () => {
    const zestyBase = env.get('zesty') as string
    const url = `${zestyBase}/documents-plain/acorns-privacy-policy-03012021/`
    getWindow().open(url, '_blank', 'noopener')
  }

  return (
    <SurveyPageBackground>
      <ActionStrip
        maxWidth={800}
        title={''}
        showMobileActions={true}
        preventDefault
        showBack={false}
        stripColor={theme.colors.primaryWhite}
        action={
          <SkipButton onClick={handleSkip}>
            {getRemoteString({
              key: 'closed_account.reopen_survey.screen.skip',
            })}
          </SkipButton>
        }
      />
      <SurveyCardContainer ref={cardRef}>
        <SurveyHeader>
          <SurveyTitle>
            {getRemoteString({
              key: 'closed_account.reopen_survey.screen.title',
            })}
          </SurveyTitle>
          <SurveySubtitle>
            {getRemoteString({
              key: 'closed_account.reopen_survey.screen.body',
            })}
          </SurveySubtitle>
        </SurveyHeader>
        <SurveyScroll ref={scrollRef}>
          {showTopFade && <Fade position="top" />}
          <SurveyList>
            {optionsOrder.map((option, index) => (
              <SurveyInputContainer
                key={`option-container-${index}`}
                isSelected={selectedIndices.has(index)}
                isMultiline={multilineIndices.has(index)}
                onClick={() => handleOptionSelect(index)}
              >
                <SurveyOptionLabel
                  ref={(el: HTMLDivElement | null) => {
                    if (el) {
                      labelRefs.current.set(index, el)
                    } else {
                      labelRefs.current.delete(index)
                    }
                  }}
                >
                  {option}
                </SurveyOptionLabel>
                <div
                  onClick={(e: React.MouseEvent<HTMLDivElement>) =>
                    e.stopPropagation()
                  }
                >
                  <SurveyCheckbox
                    id={`option-${index}`}
                    name={`option-${index}`}
                    label=""
                    checked={selectedIndices.has(index)}
                    onChange={() => handleOptionSelect(index)}
                    aria-label={option}
                  />
                </div>
              </SurveyInputContainer>
            ))}
          </SurveyList>
          {showBottomFade && <Fade position="bottom" />}
        </SurveyScroll>
        <SurveyButtonContainer ref={ctaRef}>
          <StyledDisclaimer>
            <StyledRemoteString
              stringKey={'closed_account.reopen_survey.screen.footnote'}
              wrappers={[
                {
                  component: LinkWrapper,
                  props: { onClick: handlePrivacyPolicyClick },
                },
              ]}
            />
          </StyledDisclaimer>
          <SurveyStyledButton
            onClick={handleSubmit}
            disabled={selectedIndices.size === 0}
          >
            {getRemoteString({
              key: 'closed_account.reopen_survey.screen.cta',
            })}
          </SurveyStyledButton>
        </SurveyButtonContainer>
      </SurveyCardContainer>
    </SurveyPageBackground>
  )
}
